import { useState } from 'react'
import { MatrixTypes } from '../../Generated/MatrixType.js'
import { ExtractionKits } from '../../Generated/ExtractionKit.js'
import { TrapTypes } from '../../Generated/TrapType.js'
import { CollectionTypes } from '../../Generated/CollectionType.js'
import { Sequencers } from '../../Generated/Sequencer.js'
import { SequencingChemistrys } from '../../Generated/SequencingChemistry.js'
import { LocalityTypes } from '../../Generated/LocalityType.js'
import { HabitatTypes } from '../../Generated/HabitatType.js'
import type { EnumDTOBaseType } from './HomePage/Modals/MetadataHandling/ForEnums/CSVEnumSelector.js'

interface EntryType {
  name: string
  description: string
  type: string
  enumValues?: Record<string, EnumDTOBaseType>
}

export const MetadataManual = () => {
  const [search, setSearch] = useState('')

  const metadataFields: EntryType[] = [
    { name: 'customerSampleName', description: 'Name of the sample as provided by the customer. Is used in visualization and reporting to identify the sample.', type: 'string' },
    { name: 'sampleCoordinates', description: 'Geographic coordinates where the sample was collected', type: 'object' },
    { name: 'samplingDate', description: 'Date when the sample was collected', type: 'date' },
    { name: 'samplingStart', description: 'Start time of the sampling period. Time is optional, can also just be provided as a date.', type: 'datetime' },
    { name: 'samplingEnd', description: 'End time of the sampling period. Time is optional, can also just be provided as a date.', type: 'datetime' },
    { name: 'matrixType', description: 'Type of material the sample consists of', type: 'enum', enumValues: MatrixTypes },
    { name: 'extractionKit', description: 'Kit used for DNA extraction', type: 'enum', enumValues: ExtractionKits },
    { name: 'trapType', description: 'Type of trap used for sample collection', type: 'enum', enumValues: TrapTypes },
    { name: 'collectionType', description: 'Method used for sample collection', type: 'enum', enumValues: CollectionTypes },
    { name: 'sequencer', description: 'Sequencing device used', type: 'enum', enumValues: Sequencers },
    { name: 'sequencingChemistry', description: 'Chemistry used for sequencing', type: 'enum', enumValues: SequencingChemistrys },
    { name: 'sequencingDepth', description: 'Depth of sequencing performed', type: 'string' },
    { name: 'localityType', description: 'Type of locality where the sample was collected', type: 'enum', enumValues: LocalityTypes },
    { name: 'habitatType', description: 'Type of habitat where the sample was collected. Each habitat type is related to a valid locality.', type: 'enum', enumValues: HabitatTypes },
    { name: 'forwardPrimerRequested', description: 'Forward primer requested for the sample by the customer, the lab may decide to overwrite this with a primer of their own choosing.', type: 'string' },
    { name: 'reversePrimerRequested', description: 'Reverse primer requested for the sample, the lab may decide to overwrite this with a primer of their own choosing.', type: 'string' },
  ]

  const filteredFields = metadataFields.filter(
    (field) =>
      field.name.toLowerCase().includes(search.toLowerCase()) ||
      field.description.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="p-6 bg-body-default">
      <div className="sticky top-0 bg-base-100 z-10 pb-4 rounded-sm p-4">
        <h1 className="text-2xl font-bold mb-1 text-primary">Sample Metadata Manual</h1>
        <p className="text-base mb-4 text-gray-600">
          This page outlines sample metadata supported by spcfy, the corresponding type of the data and valid values,
          if the value range is restricted.
        </p>
        <p className="text-base mb-4 text-gray-600">
          In case you are missing some kind of metadata or an option for an existing type of metadata, feel free to
          contact us and let us know.
        </p>
        <p className={"text-base mb-4 text-gray-600 italic"}>
          Metadata is used by spcfy to provide options for groupings and categorization throughout various visualizations. The more metadata your samples have attached, the easier it is to explore your biodiversity results
        </p>
        <input
          type="text"
          placeholder="Search fields..."
          className="input input-bordered w-full max-w-lg"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        {filteredFields.map((field) => (
          <div className="card border border-base-300 bg-base-100 shadow-sm hover:shadow-md transition" key={field.name}>
            <div className="card-body">
              <h3 className="card-title text-lg text-secondary">{field.name}</h3>
              <p className="text-sm text-gray-700">{field.description}</p>
              <p className="text-sm mt-2">
                <span className="font-medium text-gray-700">Type:</span> {field.type}
              </p>
              {field.type === 'enum' && <EnumList field={field} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface EnumListProps {
    field: EntryType
}

const EnumList = (props: EnumListProps) => {
  const [enumSearch, setEnumSearch] = useState('')

  if (!props.field.enumValues) return null

  const filteredEnumValues = Object.values(props.field.enumValues).filter(
    (val) =>
      val.key.toLowerCase().includes(enumSearch.toLowerCase()) ||
      val.label.toLowerCase().includes(enumSearch.toLowerCase())
  )

  return (
    <details className="mt-3 bg-base-200 p-3 rounded-md">
      <summary className="cursor-pointer font-semibold">Show Valid Values</summary>

      <input
        type="text"
        placeholder="Search enum values..."
        className="input input-sm input-bordered w-full mt-3 mb-3"
        value={enumSearch}
        onChange={(e) => setEnumSearch(e.target.value)}
      />

      <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto">
        {filteredEnumValues.length > 0 ? (
          filteredEnumValues.map((value) => (
            <div key={value.key} className="p-2 rounded-sm bg-white shadow-sm border border-base-300">
              <div className="font-semibold">{value.key}</div>
              <div className="text-sm text-gray-700">{value.label}</div>
              {'group' in value && typeof value.group === 'string' && (
                <div className="text-xs text-gray-500">Group: {value.group}</div>
              )}
              {'locality' in value && typeof value.locality === 'string' && (
                <div className="text-xs text-gray-500">Locality: {value.locality}</div>
              )}
            </div>
          ))
        ) : (
          <div className="text-sm text-gray-500 italic">No matching values found.</div>
        )}
      </div>
    </details>
  )
}